import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Power } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, LoadingRows, EmptyState, ConfirmDialog, StatusBadge } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const CATS = ["E-Commerce", "Business", "Restaurant", "Education", "Portfolio", "Blog", "Other"];
const EMPTY = { name: "", category: "E-Commerce", preview: "", font: "Manrope", radius: "8px", website_types: [], colors: { primary: "#111111", secondary: "#F4F4F5", accent: "#C9A96E", bg: "#FFFFFF", text: "#111111" }, active: true };

export default function AdminThemes() {
  const [themes, setThemes] = useState(null);
  const [cat, setCat] = useState("");
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get("/admin/themes").then((r) => setThemes(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (dialog.mode === "create") await api.post("/admin/themes", form);
      else await api.patch(`/admin/themes/${dialog.theme.id}`, form);
      toast.success("Theme saved"); setDialog(null); load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const toggle = async (t) => {
    await api.patch(`/admin/themes/${t.id}`, { ...t, active: !t.active });
    toast.success(t.active ? "Theme deactivated" : "Theme activated");
    load();
  };

  const filtered = (themes || []).filter((t) => !cat || t.category === cat);
  const cats = [...new Set((themes || []).map((t) => t.category))];

  return (
    <div data-testid="admin-themes">
      <PageHeader title="Themes" subtitle="Design system library available to partners.">
        <Button data-testid="create-theme-button" onClick={() => { setForm(EMPTY); setDialog({ mode: "create" }); }} className="rounded-full font-bold active:scale-95 transition-transform">
          <Plus className="h-4 w-4 mr-2" /> Add Theme
        </Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-8">
        <button data-testid="theme-cat-all" onClick={() => setCat("")} className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${!cat ? "bg-primary text-primary-foreground border-transparent" : "border-border hover:border-foreground/40"}`}>All</button>
        {cats.map((c) => (
          <button key={c} data-testid={`theme-cat-${c.toLowerCase()}`} onClick={() => setCat(c)} className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${cat === c ? "bg-primary text-primary-foreground border-transparent" : "border-border hover:border-foreground/40"}`}>{c}</button>
        ))}
      </div>

      {!themes ? <LoadingRows /> : filtered.length === 0 ? <EmptyState title="No themes" body="Add your first theme to the library." /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((t) => (
            <div key={t.id} data-testid={`theme-card-${t.id}`} className="bg-card border border-border rounded-md overflow-hidden group hover:shadow-xl hover:shadow-black/5 dark:hover:shadow-black/40 transition-shadow">
              <div className="aspect-[16/10] bg-muted overflow-hidden relative">
                {t.preview && <img src={t.preview} alt={t.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                <div className="absolute top-3 right-3"><StatusBadge status={t.active ? "active" : "suspended"} /></div>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display font-bold">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.category} · {t.font}</p>
                  </div>
                  <div className="flex gap-1.5">
                    {Object.values(t.colors || {}).slice(0, 4).map((c, i) => (
                      <span key={i} className="h-4 w-4 rounded-full border border-border" style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" data-testid={`theme-edit-${t.id}`} onClick={() => { setForm({ ...EMPTY, ...t, colors: { ...EMPTY.colors, ...t.colors } }); setDialog({ mode: "edit", theme: t }); }} className="rounded-full flex-1">
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" data-testid={`theme-toggle-${t.id}`} onClick={() => toggle(t)} className="rounded-full">
                    <Power className="h-3.5 w-3.5" />
                  </Button>
                  <ConfirmDialog testid={`theme-delete-${t.id}`} title="Delete theme?" description="Themes in use by websites cannot be deleted."
                    onConfirm={async () => { try { await api.delete(`/admin/themes/${t.id}`); toast.success("Theme deleted"); load(); } catch (e2) { toast.error(errMsg(e2)); } }}
                    trigger={<Button variant="outline" size="sm" className="rounded-full text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{dialog?.mode === "create" ? "Add Theme" : "Edit Theme"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2" data-testid="theme-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="overline-label">Name</Label>
                <Input data-testid="theme-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="overline-label">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="theme-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label className="overline-label">Preview image URL</Label>
              <Input data-testid="theme-preview" value={form.preview} onChange={(e) => setForm({ ...form, preview: e.target.value })} placeholder="https://…" /></div>
            <div className="grid sm:grid-cols-3 gap-3">
              {[["primary", "Primary"], ["accent", "Accent"], ["bg", "Background"]].map(([k, l]) => (
                <div key={k} className="space-y-1.5"><Label className="overline-label">{l}</Label>
                  <div className="flex items-center gap-2 border border-border rounded-md p-1.5">
                    <input type="color" data-testid={`theme-color-${k}`} value={form.colors[k]} onChange={(e) => setForm({ ...form, colors: { ...form.colors, [k]: e.target.value } })} className="h-8 w-10 cursor-pointer bg-transparent" />
                    <span className="text-xs text-muted-foreground">{form.colors[k]}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="overline-label">Font</Label>
                <Select value={form.font} onValueChange={(v) => setForm({ ...form, font: v })}>
                  <SelectTrigger data-testid="theme-font"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Manrope", "Cabinet Grotesk"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label className="overline-label">Corner radius</Label>
                <Select value={form.radius} onValueChange={(v) => setForm({ ...form, radius: v })}>
                  <SelectTrigger data-testid="theme-radius"><SelectValue /></SelectTrigger>
                  <SelectContent>{["0px", "4px", "8px", "12px", "16px", "20px"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <Button data-testid="theme-save-button" disabled={busy} className="w-full h-11 rounded-full font-bold">{busy ? "Saving…" : "Save Theme"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
